import React, {useState, useMemo} from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";

import RefreshIcon from "@mui/icons-material/Refresh";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import ContactMailIcon from "@mui/icons-material/ContactMail";

import {useInstructorLeads} from "./useInstructorLeads";

export const formatLeadDate = (isoString) => {
    if (!isoString) return "-";
    try {
        const date = new Date(isoString);
        return new Intl.DateTimeFormat("no-NO", {
            day: "numeric",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        }).format(date);
    } catch (e) {
        return isoString;
    }
};

const getStatusChipProps = (status) => {
    switch (status) {
        case "new":
            return {
                label: "Ny henvendelse",
                color: "warning",
                variant: "filled",
                sx: {fontWeight: 700, bgcolor: "#FEF3C7", color: "#92400E"},
            };
        case "contacted":
            return {
                label: "Kontaktet",
                color: "primary",
                variant: "outlined",
                sx: {fontWeight: 600, borderColor: "primary.main", color: "primary.main"},
            };
        case "archived":
            return {
                label: "Arkivert",
                variant: "outlined",
                sx: {fontWeight: 500, color: "text.secondary", borderColor: "divider"},
            };
        default:
            return {
                label: status || "Ukjent",
                variant: "outlined",
            };
    }
};

export const InstructorLeadsTable = ({instructorId}) => {
    const {leads, loading, error, updateLeadStatus, deleteLead, refresh} = useInstructorLeads({
        instructorId,
    });

    const [statusFilter, setStatusFilter] = useState("all");
    const [leadToDelete, setLeadToDelete] = useState(null);
    const [leadToViewMessage, setLeadToViewMessage] = useState(null);

    const filteredLeads = useMemo(() => {
        if (statusFilter === "all") return leads;
        return leads.filter((lead) => lead.status === statusFilter);
    }, [leads, statusFilter]);

    const counts = useMemo(() => {
        return {
            all: leads.length,
            new: leads.filter((l) => l.status === "new").length,
            contacted: leads.filter((l) => l.status === "contacted").length,
            archived: leads.filter((l) => l.status === "archived").length,
        };
    }, [leads]);

    const handleConfirmDelete = async () => {
        if (!leadToDelete) return;
        try {
            await deleteLead(leadToDelete.id);
        } finally {
            setLeadToDelete(null);
        }
    };

    return (
        <Paper
            elevation={0}
            sx={{
                p: {xs: 2.5, sm: 3.5},
                borderRadius: 3,
                border: "1px solid rgba(40, 82, 56, 0.12)",
                bgcolor: "#FFFFFF",
            }}
        >
            {/* Header & Controls */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: {xs: "flex-start", sm: "center"},
                    flexDirection: {xs: "column", sm: "row"},
                    gap: 1.5,
                    mb: 2.5,
                }}
            >
                <Box sx={{display: "flex", alignItems: "center", gap: 1.5}}>
                    <Box
                        sx={{
                            display: "inline-flex",
                            p: 1,
                            borderRadius: 2,
                            bgcolor: "rgba(40, 82, 56, 0.08)",
                            color: "primary.main",
                        }}
                    >
                        <ContactMailIcon fontSize="medium" />
                    </Box>
                    <Box>
                        <Typography
                            variant="h5"
                            component="h2"
                            sx={{fontWeight: 700, color: "primary.main", fontSize: "1.3rem"}}
                        >
                            Mottatte kurshenvendelser
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Interessenter som har meldt interesse for dine kommende kurs
                        </Typography>
                    </Box>
                </Box>

                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={refresh}
                    disabled={loading}
                    sx={{
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 600,
                        minHeight: 44,
                        "&:focus-visible": {
                            outline: "2px solid #285238",
                            outlineOffset: "2px",
                        },
                    }}
                >
                    Oppdater
                </Button>
            </Box>

            {error && (
                <Alert severity="error" sx={{mb: 2.5}}>
                    {error}
                </Alert>
            )}

            {/* Filter Tabs */}
            <Box sx={{borderBottom: 1, borderColor: "divider", mb: 2.5}}>
                <Tabs
                    value={statusFilter}
                    onChange={(_, val) => setStatusFilter(val)}
                    textColor="primary"
                    indicatorColor="primary"
                    aria-label="Filtrer henvendelser etter status"
                    sx={{minHeight: 44}}
                >
                    <Tab
                        value="all"
                        label={`Alle (${counts.all})`}
                        sx={{minHeight: 44, textTransform: "none", fontWeight: 600}}
                    />
                    <Tab
                        value="new"
                        label={`Nye (${counts.new})`}
                        sx={{minHeight: 44, textTransform: "none", fontWeight: 600}}
                    />
                    <Tab
                        value="contacted"
                        label={`Kontaktet (${counts.contacted})`}
                        sx={{minHeight: 44, textTransform: "none", fontWeight: 600}}
                    />
                    <Tab
                        value="archived"
                        label={`Arkivert (${counts.archived})`}
                        sx={{minHeight: 44, textTransform: "none", fontWeight: 600}}
                    />
                </Tabs>
            </Box>

            {/* Table Content */}
            {loading ? (
                <Box sx={{display: "flex", justifyContent: "center", py: 6}}>
                    <CircularProgress color="primary" />
                </Box>
            ) : filteredLeads.length === 0 ? (
                <Box
                    sx={{
                        textAlign: "center",
                        py: 5,
                        px: 2,
                        borderRadius: 2.5,
                        bgcolor: "rgba(40, 82, 56, 0.02)",
                        border: "1px dashed rgba(40, 82, 56, 0.2)",
                    }}
                >
                    <Typography variant="subtitle1" sx={{fontWeight: 600, color: "text.primary", mb: 0.5}}>
                        {statusFilter === "all"
                            ? "Ingen henvendelser mottatt enda"
                            : `Ingen henvendelser med status "${statusFilter}"`}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Når innbyggere klikker «Meld interesse» på din profil eller dine planlagte kurs, vil
                        henvendelsen dukke opp her.
                    </Typography>
                </Box>
            ) : (
                <TableContainer sx={{borderRadius: 2, border: "1px solid rgba(0,0,0,0.06)"}}>
                    <Table aria-label="Tabell over mottatte kurshenvendelser">
                        <TableHead sx={{bgcolor: "rgba(40, 82, 56, 0.04)"}}>
                            <TableRow>
                                <TableCell sx={{fontWeight: 700}}>Mottatt</TableCell>
                                <TableCell sx={{fontWeight: 700}}>Navn & Kontakt</TableCell>
                                <TableCell sx={{fontWeight: 700}}>Ønsket kurs</TableCell>
                                <TableCell sx={{fontWeight: 700}}>Melding</TableCell>
                                <TableCell sx={{fontWeight: 700}}>Status</TableCell>
                                <TableCell align="right" sx={{fontWeight: 700}}>
                                    Handlinger
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredLeads.map((lead) => {
                                const chipProps = getStatusChipProps(lead.status);

                                return (
                                    <TableRow key={lead.id} hover>
                                        <TableCell sx={{whiteSpace: "nowrap", fontSize: "0.875rem", color: "text.secondary"}}>
                                            {formatLeadDate(lead.submittedAt || lead.createdAt)}
                                        </TableCell>

                                        <TableCell>
                                            <Typography variant="subtitle2" sx={{fontWeight: 700, color: "primary.main"}}>
                                                {lead.name}
                                            </Typography>
                                            <Box sx={{display: "flex", flexWrap: "wrap", gap: 1.5, mt: 0.5}}>
                                                {lead.email && (
                                                    <Box
                                                        component="a"
                                                        href={`mailto:${lead.email}`}
                                                        sx={{
                                                            display: "inline-flex",
                                                            alignItems: "center",
                                                            gap: 0.5,
                                                            fontSize: "0.8rem",
                                                            color: "text.secondary",
                                                            textDecoration: "none",
                                                            "&:hover": {color: "primary.main", textDecoration: "underline"},
                                                        }}
                                                    >
                                                        <EmailOutlinedIcon sx={{fontSize: 14}} />
                                                        {lead.email}
                                                    </Box>
                                                )}
                                                {lead.phone && (
                                                    <Box
                                                        component="a"
                                                        href={`tel:${lead.phone}`}
                                                        sx={{
                                                            display: "inline-flex",
                                                            alignItems: "center",
                                                            gap: 0.5,
                                                            fontSize: "0.8rem",
                                                            color: "text.secondary",
                                                            textDecoration: "none",
                                                            "&:hover": {color: "primary.main", textDecoration: "underline"},
                                                        }}
                                                    >
                                                        <PhoneOutlinedIcon sx={{fontSize: 14}} />
                                                        {lead.phone}
                                                    </Box>
                                                )}
                                            </Box>
                                        </TableCell>

                                        <TableCell>
                                            <Typography variant="body2" sx={{fontWeight: 600}}>
                                                {lead.courseTitle || "Generell henvendelse"}
                                            </Typography>
                                        </TableCell>

                                        <TableCell sx={{maxWidth: 220}}>
                                            {lead.message ? (
                                                <Button
                                                    size="small"
                                                    variant="text"
                                                    onClick={() => setLeadToViewMessage(lead)}
                                                    sx={{
                                                        textTransform: "none",
                                                        p: 0.5,
                                                        borderRadius: 1,
                                                        color: "text.secondary",
                                                        textAlign: "left",
                                                        fontSize: "0.85rem",
                                                        display: "-webkit-box",
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: "vertical",
                                                        overflow: "hidden",
                                                        "&:focus-visible": {
                                                            outline: "2px solid #285238",
                                                            outlineOffset: "2px",
                                                        },
                                                    }}
                                                >
                                                    {lead.message}
                                                </Button>
                                            ) : (
                                                <Typography variant="caption" color="text.secondary">
                                                    Ingen melding
                                                </Typography>
                                            )}
                                        </TableCell>

                                        <TableCell>
                                            <Chip size="small" {...chipProps} />
                                        </TableCell>

                                        <TableCell align="right">
                                            <Box sx={{display: "flex", justifyContent: "flex-end", gap: 0.5}}>
                                                {lead.status !== "contacted" && (
                                                    <Tooltip title="Merk som kontaktet">
                                                        <IconButton
                                                            size="small"
                                                            color="primary"
                                                            aria-label={`Merk som kontaktet for ${lead.name}`}
                                                            onClick={() => updateLeadStatus(lead.id, "contacted")}
                                                            sx={{
                                                                minWidth: 44,
                                                                minHeight: 44,
                                                                "&:focus-visible": {
                                                                    outline: "2px solid #285238",
                                                                    outlineOffset: "2px",
                                                                },
                                                            }}
                                                        >
                                                            <MarkEmailReadOutlinedIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}

                                                {lead.status !== "archived" && (
                                                    <Tooltip title="Arkiver">
                                                        <IconButton
                                                            size="small"
                                                            aria-label={`Arkiver henvendelse fra ${lead.name}`}
                                                            onClick={() => updateLeadStatus(lead.id, "archived")}
                                                            sx={{
                                                                color: "text.secondary",
                                                                minWidth: 44,
                                                                minHeight: 44,
                                                                "&:focus-visible": {
                                                                    outline: "2px solid #285238",
                                                                    outlineOffset: "2px",
                                                                },
                                                            }}
                                                        >
                                                            <ArchiveOutlinedIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}

                                                {lead.status === "archived" && (
                                                    <Tooltip title="Gjenåpne henvendelse">
                                                        <IconButton
                                                            size="small"
                                                            aria-label={`Gjenåpne henvendelse fra ${lead.name}`}
                                                            onClick={() => updateLeadStatus(lead.id, "new")}
                                                            sx={{
                                                                color: "warning.main",
                                                                minWidth: 44,
                                                                minHeight: 44,
                                                                "&:focus-visible": {
                                                                    outline: "2px solid #d97746",
                                                                    outlineOffset: "2px",
                                                                },
                                                            }}
                                                        >
                                                            <CheckCircleOutlineIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}

                                                <Tooltip title="Slett henvendelse (GDPR)">
                                                    <IconButton
                                                        size="small"
                                                        aria-label={`Slett henvendelse fra ${lead.name}`}
                                                        onClick={() => setLeadToDelete(lead)}
                                                        sx={{
                                                            color: "error.main",
                                                            minWidth: 44,
                                                            minHeight: 44,
                                                            "&:hover": {bgcolor: "rgba(211, 47, 47, 0.08)"},
                                                            "&:focus-visible": {
                                                                outline: "2px solid #d32f2f",
                                                                outlineOffset: "2px",
                                                            },
                                                        }}
                                                    >
                                                        <DeleteOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Read Full Message Dialog */}
            <Dialog
                open={Boolean(leadToViewMessage)}
                onClose={() => setLeadToViewMessage(null)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle component="div">
                    <Typography variant="h6" sx={{fontWeight: 700}}>
                        Melding fra {leadToViewMessage?.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {leadToViewMessage?.courseTitle} • {formatLeadDate(leadToViewMessage?.submittedAt || leadToViewMessage?.createdAt)}
                    </Typography>
                </DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body1" sx={{whiteSpace: "pre-wrap", lineHeight: 1.7}}>
                        {leadToViewMessage?.message}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{px: 2.5, py: 1.5}}>
                    <Button
                        onClick={() => setLeadToViewMessage(null)}
                        sx={{
                            textTransform: "none",
                            minHeight: 44,
                            fontWeight: 600,
                            "&:focus-visible": {
                                outline: "2px solid #285238",
                                outlineOffset: "2px",
                            },
                        }}
                    >
                        Lukk
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirm Permanent Delete Dialog (GDPR) */}
            <Dialog
                open={Boolean(leadToDelete)}
                onClose={() => setLeadToDelete(null)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle component="div">
                    <Typography variant="h6" sx={{fontWeight: 700, color: "error.main"}}>
                        Slett kurshenvendelse
                    </Typography>
                </DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" sx={{mb: 1.5}}>
                        Er du sikker på at du vil slette henvendelsen fra{" "}
                        <strong>{leadToDelete?.name}</strong>?
                    </Typography>
                    <Alert severity="warning">
                        Handlingen er permanent i tråd med GDPR (retten til sletting). Personopplysningene slettes
                        umiddelbart fra databasen.
                    </Alert>
                </DialogContent>
                <DialogActions sx={{px: 2.5, py: 1.5}}>
                    <Button
                        onClick={() => setLeadToDelete(null)}
                        color="inherit"
                        sx={{
                            textTransform: "none",
                            minHeight: 44,
                            fontWeight: 600,
                            "&:focus-visible": {
                                outline: "2px solid #285238",
                                outlineOffset: "2px",
                            },
                        }}
                    >
                        Avbryt
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        color="error"
                        variant="contained"
                        sx={{
                            textTransform: "none",
                            fontWeight: 700,
                            minHeight: 44,
                            "&:focus-visible": {
                                outline: "2px solid #d32f2f",
                                outlineOffset: "2px",
                            },
                        }}
                    >
                        Ja, slett henvendelse
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};
